import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { MotionHighlight, MotionHighlightItem } from '../shared/motion-highlight';
import {
  House,
  GlobeHemisphereWest,
  SignOut,
  CaretLeft,
  CaretRight,
  Moon,
  Sun,
} from 'phosphor-react';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useTheme } from '../../../contexts/ThemeContext';

const TRANSITION = 'transition-[all_0.4s_cubic-bezier(0.4,0,0.2,1)]';

const Sidebar = () => {
  const [userData, setUserData] = useState(null);
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredPath, setHoveredPath] = useState(null);

  const menuItems = [
    { name: 'Dashboard', path: '/learner/dashboard', icon: House },
    { name: 'German', path: '/learner/language-learning', icon: GlobeHemisphereWest, flag: '/Flag/De.png' },
    { name: 'English', path: '/learner/english-language-learning', icon: GlobeHemisphereWest, flag: '/Flag/En.svg' },
    { name: 'Japanese', path: '/learner/japanese-language-learning', icon: GlobeHemisphereWest, flag: '/Flag/Ja.png' },
  ];

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    console.log('👋 Logging out...');
    navigate('/learner/login');
  };

  const navItemBase = `flex items-center my-0.5 rounded-[10px] text-white/70 no-underline ${TRANSITION} text-sm font-medium relative z-[1] whitespace-nowrap hover:text-white/90`;
  const navItemCollapsed = isCollapsed ? 'justify-center items-center p-2.5 w-9 overflow-visible' : 'py-2 px-3 overflow-hidden';
  const navItemActive = (isActive) => (isActive ? '!text-[#1d4746] !bg-[#fcb319] rounded-[10px]' : '');

  const iconCls = `text-xl flex items-center justify-center w-5 shrink-0 ${isCollapsed ? '' : 'mr-2.5'}`;
  const textCls = `flex-1 ${TRANSITION} ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`;

  const footerBtnBase = 'flex items-center justify-center bg-white/5 border border-white/10 rounded-lg text-white/70 text-[13px] font-medium cursor-pointer overflow-hidden whitespace-nowrap hover:bg-white/10 hover:text-white/90 hover:border-white/20';
  const footerBtnCollapsed = isCollapsed ? 'p-2.5 gap-0 w-9 min-w-9 max-w-9' : 'w-full gap-1.5 py-2 px-3';

  return (
    <>
      <div
        className={`fixed left-0 top-0 z-[1000] h-screen flex flex-col overflow-x-hidden ${TRANSITION} max-md:hidden ${
          isCollapsed ? 'w-[50px]' : 'w-[190px]'
        }`}
        style={{ background: 'var(--sidebar-gradient)' }}
      >
        <div
          className={`flex flex-col gap-1 py-4 border-b border-white/10 ${isCollapsed ? 'px-0 items-center justify-center' : 'px-4'}`}
        >
          <img
            src={isCollapsed ? '/ZenawanLogo/zenawan-z-yellow.png' : '/ZenVokus-New/Label Persegi Panjang Lautan (1).png'}
            alt="ZenVokus Logo"
            className={`h-auto ${TRANSITION} ${isCollapsed ? 'w-9 block mx-auto self-center' : 'w-[125px]'}`}
          />
        </div>

        <nav
          className={`flex-1 py-3 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-white/30 ${
            isCollapsed ? 'flex flex-col items-center py-3' : ''
          }`}
        >
          <MotionHighlight
            mode="parent"
            controlledItems={true}
            value={hoveredPath}
            className="bg-white/10 rounded-[10px]"
            containerClassName={isCollapsed ? 'flex flex-col items-center w-full p-0 overflow-hidden' : 'flex flex-col w-full px-2.5'}
            transition={{ type: 'spring', stiffness: 400, damping: 40 }}
          >
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              return (
                <MotionHighlightItem key={item.path} value={item.path} asChild>
                  <NavLink
                    to={item.path}
                    className={({ isActive: active }) =>
                      `${navItemBase} ${navItemCollapsed} ${navItemActive(active)}`
                    }
                    onMouseEnter={() => setHoveredPath(item.path)}
                    onMouseLeave={() => setHoveredPath(null)}
                  >
                    <span className={`${iconCls} ${isActive ? '!text-[#1d4746]' : ''}`}>
                      {item.flag ? (
                        <img src={item.flag} alt="" aria-hidden width={20} height={15} className="object-contain" />
                      ) : (
                        <IconComponent size={20} weight="regular" />
                      )}
                    </span>
                    <span className={textCls}>{item.name}</span>
                  </NavLink>
                </MotionHighlightItem>
              );
            })}
          </MotionHighlight>
        </nav>

        <div
          className={`py-4 border-t border-white/10 ${TRANSITION} ${isCollapsed ? 'px-2 flex flex-col items-center' : 'px-4'}`}
        >
          <div
            className={`flex items-center overflow-hidden mb-2.5 ${TRANSITION} ${isCollapsed ? 'justify-center gap-0' : 'gap-2.5'}`}
          >
            <img
              src={userData?.picture || 'https://via.placeholder.com/40'}
              alt="Profile"
              className="w-9 h-9 rounded-full object-cover border-2 border-[#fcb319] shrink-0"
            />
            <div className={`flex-1 overflow-hidden ${TRANSITION} ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>
              <p className="text-white text-[13px] font-semibold m-0 whitespace-nowrap overflow-hidden text-ellipsis">
                {userData?.name || 'Fellycia'}
              </p>
              <p className="text-white/50 text-[11px] mt-0.5 m-0 whitespace-nowrap overflow-hidden text-ellipsis">
                {userData?.role || 'Learner'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={toggleDarkMode}
            className={`${footerBtnBase} ${footerBtnCollapsed} mb-2`}
            aria-label="Toggle dark mode"
          >
            <span className="text-base flex items-center justify-center shrink-0">
              {isDarkMode ? <Sun size={20} weight="regular" /> : <Moon size={20} weight="regular" />}
            </span>
            <span className={textCls}>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          <button type="button" onClick={handleLogout} className={`${footerBtnBase} ${footerBtnCollapsed}`}>
            <span className="text-base flex items-center justify-center shrink-0">
              <SignOut size={20} weight="regular" />
            </span>
            <span className={textCls}>Log Out</span>
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={toggleSidebar}
        className="fixed top-1/2 w-10 h-10 min-w-10 min-h-10 bg-[#fcb319] border-none rounded-full cursor-pointer flex items-center justify-center text-[#1d4746] text-lg shadow-[-2px_0_8px_rgba(0,0,0,0.2)] z-[1001] transition-[all_0.4s_cubic-bezier(0.4,0,0.2,1)] hover:bg-[#ffc241] hover:scale-105 active:scale-95 max-md:hidden shrink-0"
        style={{ left: isCollapsed ? 50 : 175, transform: `translateX(-50%) translateY(-50%)` }}
      >
        {isCollapsed ? <CaretRight size={18} weight="bold" /> : <CaretLeft size={18} weight="bold" />}
      </button>
    </>
  );
};

export default Sidebar;

